/**
 * SoleadLab 2.0 - Tasks Panel
 *
 * Floating panel for issue-to-task workflow management.
 * Two-column layout: Issues sidebar (left) + Tasks main area (right).
 *
 * Issues are synced from indications and monitoring alarms.
 * Tasks are created from issues for maintenance scheduling.
 */

import { useState, useMemo } from 'react';
import { useTaskStore, useProjectStore, useMapStore } from '../../stores';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import { Button } from '../common';
import IssueCard from './IssueCard';
import TaskCard from './TaskCard';
import IssueDrawer from './IssueDrawer';
import CreateTaskModal from './CreateTaskModal';
import EmailTaskModal from './EmailTaskModal';
import type { Task, TasksTabId, IssueFilterId } from '@/types/tasks';
import './TasksPanel.css';

interface TasksPanelProps {
  onClose: () => void;
}

export default function TasksPanel({ onClose }: TasksPanelProps) {
  // F-02E-2B: share viewers see no Create Task, no batch tools, no
  // Sync-from-Indications / Sync-from-Alarms (mutations), and TaskCards
  // render without onEdit/onComplete/onCancel/onEmail actions.
  const isReadOnly = useIsReadOnlyViewer();
  // ── Task Store (individual selectors) ──────────────────────────────
  const issues = useTaskStore((s) => s.issues);
  const tasks = useTaskStore((s) => s.tasks);
  const auditLog = useTaskStore((s) => s.auditLog);
  const selectedIssueId = useTaskStore((s) => s.selectedIssueId);
  const activeTab = useTaskStore((s) => s.activeTab);
  const issueFilter = useTaskStore((s) => s.issueFilter);
  const batchMode = useTaskStore((s) => s.batchMode);
  const selectedTaskIds = useTaskStore((s) => s.selectedTaskIds);

  const syncFromIndications = useTaskStore((s) => s.syncFromIndications);
  const syncFromAlarms = useTaskStore((s) => s.syncFromAlarms);
  const selectIssue = useTaskStore((s) => s.selectIssue);
  const setIssueFilter = useTaskStore((s) => s.setIssueFilter);
  const setActiveTab = useTaskStore((s) => s.setActiveTab);
  const acknowledgeIssue = useTaskStore((s) => s.acknowledgeIssue);
  const suppressIssue = useTaskStore((s) => s.suppressIssue);
  const reopenIssue = useTaskStore((s) => s.reopenIssue);
  const createTaskFromIssues = useTaskStore((s) => s.createTaskFromIssues);
  const updateTask = useTaskStore((s) => s.updateTask);
  const completeTask = useTaskStore((s) => s.completeTask);
  const cancelTask = useTaskStore((s) => s.cancelTask);
  const toggleBatchMode = useTaskStore((s) => s.toggleBatchMode);
  const toggleTaskSelection = useTaskStore((s) => s.toggleTaskSelection);
  const selectAllTasks = useTaskStore((s) => s.selectAllTasks);
  const clearBatchSelection = useTaskStore((s) => s.clearBatchSelection);

  // ── Project & Map Store ────────────────────────────────────────────
  const indications = useProjectStore((s) => s.data.indications);
  const siteId = useProjectStore((s) => s.meta.siteId);
  const flyTo = useMapStore((s) => s.flyTo);

  // ── Local UI State ─────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [linkedIssueIdsForCreate, setLinkedIssueIdsForCreate] = useState<string[]>([]);
  const [emailTasks, setEmailTasks] = useState<Task[]>([]);

  // ── Derived Data ───────────────────────────────────────────────────
  const selectedIssue = useMemo(
    () => issues.find((i) => i.issueId === selectedIssueId) ?? null,
    [issues, selectedIssueId]
  );

  const filteredIssues = useMemo(() => {
    switch (issueFilter) {
      case 'open':     return issues.filter((i) => i.state === 'Open');
      case 'critical': return issues.filter((i) => i.severity === 'Critical' && i.state !== 'Closed' && i.state !== 'Suppressed');
      case 'major':    return issues.filter((i) => i.severity === 'Major' && i.state !== 'Closed' && i.state !== 'Suppressed');
      default:         return issues;
    }
  }, [issues, issueFilter]);

  const activeTasks = useMemo(
    () => tasks.filter((t) => t.status !== 'Done' && t.status !== 'Canceled'),
    [tasks]
  );

  const completedTasks = useMemo(
    () => tasks.filter((t) => t.status === 'Done' || t.status === 'Canceled'),
    [tasks]
  );

  // Stats
  const openIssueCount = useMemo(() => issues.filter((i) => i.state === 'Open').length, [issues]);
  const activeTaskCount = activeTasks.length;
  const overdueCount = useMemo(() => {
    const now = Date.now();
    return activeTasks.filter((t) => t.sla.resolveBy && new Date(t.sla.resolveBy).getTime() < now).length;
  }, [activeTasks]);
  const totalImpact = useMemo(() => {
    let total = 0;
    for (const issue of issues) {
      if (issue.state === 'Open' || issue.state === 'Acknowledged') {
        total += issue.estimatedImpact.revenueLoss;
      }
    }
    return total;
  }, [issues]);

  // ── Handlers ───────────────────────────────────────────────────────
  const handleSync = () => {
    const count = syncFromIndications(indications, siteId || '');
    console.log(`Synced ${count} new issues from indications`);
  };

  const handleSyncAlarms = () => {
    const count = syncFromAlarms();
    console.log(`Synced ${count} new issues from alarms`);
  };

  const handleCreateTaskFromIssue = () => {
    if (!selectedIssueId) return;
    setLinkedIssueIdsForCreate([selectedIssueId]);
    setShowCreateModal(true);
  };

  const handleSaveTask = (taskData: Partial<Task>) => {
    if (editingTask) {
      updateTask(editingTask.taskId, taskData);
    } else {
      createTaskFromIssues(linkedIssueIdsForCreate, taskData);
    }
    setShowCreateModal(false);
    setEditingTask(null);
    setLinkedIssueIdsForCreate([]);
    selectIssue(null);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setLinkedIssueIdsForCreate(task.linkedIssueIds);
    setShowCreateModal(true);
  };

  const handleZoomToIssue = () => {
    if (selectedIssue?.geo) {
      flyTo([selectedIssue.geo.lng, selectedIssue.geo.lat], 18);
    }
  };

  const handleSuppressIssue = () => {
    if (selectedIssueId) {
      suppressIssue(selectedIssueId, 'Manual suppression', '', '');
    }
  };

  const handleCreateNew = () => {
    setEditingTask(null);
    setLinkedIssueIdsForCreate([]);
    setShowCreateModal(true);
  };

  const handleEmailTask = (task: Task) => {
    setEmailTasks([task]);
  };

  const handleBatchWorkOrder = () => {
    const selected = tasks.filter((t) => selectedTaskIds.has(t.taskId));
    if (selected.length > 0) setEmailTasks(selected);
  };

  // ── Tab content ────────────────────────────────────────────────────
  const tabTasks = activeTab === 'completed' ? completedTasks : activeTasks;

  const linkedIssuesForModal = useMemo(
    () => issues.filter((i) => linkedIssueIdsForCreate.includes(i.issueId)),
    [issues, linkedIssueIdsForCreate]
  );

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="tp-overlay">
      <div className="tp-container">
        {/* Header */}
        <div className="tp-header">
          <div className="tp-header__left">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            <span className="tp-header__title">Tasks & Issues</span>
          </div>
          <div className="tp-header__right">
            <button className="tp-close-btn" onClick={onClose} title="Close panel">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="tp-stats">
          <div className="tp-stat">
            <div className="tp-stat__value">{openIssueCount}</div>
            <div className="tp-stat__label">Open Issues</div>
          </div>
          <div className="tp-stat">
            <div className="tp-stat__value">{activeTaskCount}</div>
            <div className="tp-stat__label">Active Tasks</div>
          </div>
          <div className="tp-stat">
            <div className="tp-stat__value" style={{ color: overdueCount > 0 ? '#dc2626' : undefined }}>{overdueCount}</div>
            <div className="tp-stat__label">Overdue</div>
          </div>
          <div className="tp-stat">
            <div className="tp-stat__value">${totalImpact > 0 ? (totalImpact >= 1000 ? `${(totalImpact / 1000).toFixed(1)}k` : totalImpact.toFixed(0)) : '0'}</div>
            <div className="tp-stat__label">Est. Impact</div>
          </div>
        </div>

        {/* Body */}
        <div className="tp-body">
          {/* Issues Sidebar */}
          <div className="tp-sidebar">
            <div className="tp-sidebar__header">
              <span className="tp-sidebar__title">Issues Inbox</span>
              <span className="tp-sidebar__count">{filteredIssues.length}</span>
            </div>

            {/* Filters */}
            <div className="tp-filters">
              {(['all', 'open', 'critical', 'major'] as IssueFilterId[]).map((f) => (
                <button
                  key={f}
                  className={`tp-filter-btn${issueFilter === f ? ' tp-filter-btn--active' : ''}`}
                  onClick={() => setIssueFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            {/* Sync buttons — hidden for share viewers (mutations) */}
            {!isReadOnly && (
              <div className="tp-sidebar__actions">
                <Button variant="ghost" size="sm" onClick={handleSync}>
                  Sync from Indications
                </Button>
                <Button variant="ghost" size="sm" onClick={handleSyncAlarms}>
                  Sync from Alarms
                </Button>
              </div>
            )}

            {/* Issue list */}
            <div className="tp-issue-list">
              {filteredIssues.length === 0 ? (
                <div className="tp-empty">
                  <span>No issues found</span>
                  <span className="tp-empty__hint">Click "Sync from Indications" to import</span>
                </div>
              ) : (
                filteredIssues.map((issue) => (
                  <IssueCard
                    key={issue.issueId}
                    issue={issue}
                    isSelected={issue.issueId === selectedIssueId}
                    onClick={() => selectIssue(issue.issueId === selectedIssueId ? null : issue.issueId)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Main Area */}
          <div className="tp-main">
            {/* Tabs + actions */}
            <div className="tp-main__header">
              <div className="tp-tabs">
                {([
                  { id: 'tasks' as TasksTabId, label: 'Tasks' },
                  { id: 'completed' as TasksTabId, label: 'Completed' },
                  { id: 'audit' as TasksTabId, label: 'Audit Log' },
                ]).map((tab) => (
                  <button
                    key={tab.id}
                    className={`tp-tab${activeTab === tab.id ? ' tp-tab--active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="tp-main__actions">
                {activeTab !== 'audit' && !isReadOnly && (
                  <>
                    <Button variant="ghost" size="sm" onClick={toggleBatchMode}>
                      {batchMode ? 'Cancel Batch' : 'Batch'}
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleCreateNew} data-testid="tasks-panel-create">
                      + Create Task
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Batch bar */}
            {batchMode && (
              <div className="tp-batch-bar">
                <span>{selectedTaskIds.size} selected</span>
                <Button variant="ghost" size="sm" onClick={selectAllTasks}>Select All</Button>
                <Button variant="ghost" size="sm" onClick={clearBatchSelection}>Clear</Button>
                {selectedTaskIds.size > 0 && (
                  <Button variant="primary" size="sm" onClick={handleBatchWorkOrder}>
                    Send Work Order
                  </Button>
                )}
              </div>
            )}

            {/* Content */}
            <div className="tp-task-list">
              {activeTab === 'audit' ? (
                auditLog.length === 0 ? (
                  <div className="tp-empty"><span>No audit entries yet</span></div>
                ) : (
                  <div className="tp-audit-list">
                    {[...auditLog].reverse().map((entry, i) => (
                      <div key={i} className="tp-audit-entry">
                        <span className="tp-audit-entry__time">
                          {new Date(entry.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="tp-audit-entry__action">{entry.action}</span>
                        <span className="tp-audit-entry__details">{entry.details}</span>
                      </div>
                    ))}
                  </div>
                )
              ) : tabTasks.length === 0 ? (
                <div className="tp-empty">
                  <span>{activeTab === 'completed' ? 'No completed tasks' : 'No active tasks'}</span>
                  <span className="tp-empty__hint">Create tasks from issues in the sidebar</span>
                </div>
              ) : (
                tabTasks.map((task) => (
                  <TaskCard
                    key={task.taskId}
                    task={task}
                    /* F-02E-2B: share viewers see TaskCards with inert
                       handlers. Hiding the per-card buttons would
                       require editing TaskCard (out of allowlist);
                       the no-op handlers prevent local mutations
                       and keep the list visible as read-only data.
                       Backend rejects underlying writes anyway. The
                       per-card Edit/Complete/Cancel/Email buttons
                       remaining visible is documented F-02E-2C
                       cleanup (add TaskCard to allowlist + hide
                       the action row). */
                    batchMode={isReadOnly ? false : batchMode}
                    isSelected={selectedTaskIds.has(task.taskId)}
                    onEdit={isReadOnly ? () => {} : () => handleEditTask(task)}
                    onComplete={isReadOnly ? () => {} : () => completeTask(task.taskId)}
                    onCancel={isReadOnly ? () => {} : () => cancelTask(task.taskId)}
                    onEmail={isReadOnly ? () => {} : () => handleEmailTask(task)}
                    onToggleSelect={isReadOnly ? () => {} : () => toggleTaskSelection(task.taskId)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Issue Drawer (slides in from right within body).
              F-02E-2B: IssueDrawer is in the allowlist and gates its own
              action buttons; we still pass functional handlers so the
              authenticated owner flow stays intact. */}
          {selectedIssue && (
            <IssueDrawer
              issue={selectedIssue}
              onClose={() => selectIssue(null)}
              onCreateTask={handleCreateTaskFromIssue}
              onAcknowledge={() => acknowledgeIssue(selectedIssueId!)}
              onZoomToMap={handleZoomToIssue}
              onSuppress={handleSuppressIssue}
              onReopen={() => reopenIssue(selectedIssueId!)}
            />
          )}
        </div>
      </div>

      {/* Create/Edit Task Modal */}
      <CreateTaskModal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setEditingTask(null); }}
        onSave={handleSaveTask}
        linkedIssues={linkedIssuesForModal}
        editingTask={editingTask}
      />

      {/* Email Task Modal */}
      <EmailTaskModal
        isOpen={emailTasks.length > 0}
        onClose={() => setEmailTasks([])}
        tasks={emailTasks}
        allIssues={issues}
      />
    </div>
  );
}
